// Ajout d'un commentaire pour forcer la re-vérification
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/auth';
import { useLanguage } from '@/context/language-context';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { fetchEscortProfileById } from '@/services/profileService';
import { EscortProfile } from '@/types';
import { useToast } from '@/hooks/use-toast';
import BasicInfoTab from './BasicInfoTab';
import ServicesTab from './ServicesTab';
import RatesTab from './RatesTab';
import VisibilityTab from './VisibilityTab';
import AiAssistantTab from './AiAssistantTab';
import CreateEscortProfileForm from './CreateEscortProfileForm';
import { Button } from '@/components/ui/button';

const EscortProfileManager: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  const [profile, setProfile] = useState<EscortProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadProfileData = async () => {
    if (!user?.escortProfileId) {
      setIsLoading(false);
      console.warn("User is logged in but escortProfileId not found. Should render creation form.");
      setProfile(null);
      return;
    }

    try {
      setIsLoading(true);
      const escortProfile = await fetchEscortProfileById(user.escortProfileId);
      if (escortProfile) {
        setProfile(escortProfile);
      } else {
        setProfile(null);
        console.log("Aucun profil d'escort existant trouvé pour cet utilisateur.");
      }
    } catch (error) {
      console.error('Error loading escort profile:', error);
      setLoadError((error instanceof Error && error.message) || 'Unknown error loading profile');
      setProfile(null);
      toast({
        title: t('escort:profile.error_loading'),
        description: t('escort:profile.try_again_later') + ((error instanceof Error && error.message) ? `: ${error.message}` : ''),
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadProfileDataCallback = useCallback(loadProfileData, [user?.escortProfileId, toast, t]);

  const handleProfileUpdate = (updatedProfileData: Partial<EscortProfile>) => {
    setProfile(prevProfile => {
      if (!prevProfile) return null;
      return { ...prevProfile, ...updatedProfileData };
    });
  };

  useEffect(() => {
    loadProfileDataCallback();
  }, [user, loadProfileDataCallback]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-6 text-center text-red-600">
        <p>{t('escort:profile.error_loading')}</p>
        <p>{loadError}</p>
        <Button onClick={loadProfileDataCallback} className="mt-4">
          {t('common:retry')}
        </Button>
      </div>
    );
  }

  if (!profile && user?.id) {
    return <CreateEscortProfileForm userId={user.id} onProfileCreated={loadProfileDataCallback} />;
  }

  if (profile) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">{t('escort:profile.manage_profile')}</h1>
          <p className="text-muted-foreground">{t('escort:profile.profile_description')}</p>
        </div>

        <Card>
          <Tabs defaultValue="basic-info" className="w-full">
            <TabsList className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 lg:w-full">
              <TabsTrigger value="basic-info">{t('escort:profile.basic_info')}</TabsTrigger>
              <TabsTrigger value="services">{t('escort:profile.services')}</TabsTrigger>
              <TabsTrigger value="rates">{t('escort:profile.rates')}</TabsTrigger>
              <TabsTrigger value="visibility">{t('escort:profile.visibility')}</TabsTrigger>
              <TabsTrigger value="ai-assistant">{t('escort:profile.ai.tab_title')}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic-info">
              <BasicInfoTab profile={profile} onProfileUpdate={handleProfileUpdate} />
            </TabsContent>
            
            <TabsContent value="services">
              <ServicesTab escortId={profile.id} />
            </TabsContent>
            
            <TabsContent value="rates">
              <RatesTab escortId={profile.id} />
            </TabsContent>
            
            <TabsContent value="visibility">
              <VisibilityTab profile={profile} onProfileUpdate={handleProfileUpdate} />
            </TabsContent>
            
            <TabsContent value="ai-assistant">
              <AiAssistantTab profile={profile} onProfileUpdate={handleProfileUpdate} />
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 text-center text-muted-foreground">
      {t('escort:profile.unable_to_load_or_create')}
    </div>
  );
};

export default EscortProfileManager;
